import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, X } from "lucide-react";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";

/**
 * Modal for a server owner to edit their listing after submission.
 * Bound to the existing `PATCH /api/servers/{id}` endpoint (ServerUpdate model)
 * which permits: description, long_description, categories, tags, banner_url.
 *
 * Name / icon / member count / invite come from Discord itself and stay
 * read-only — no reason to let the owner override Discord's own data.
 */
export default function EditServerDialog({ server, open, onClose, onSaved }) {
  const [description, setDescription] = useState("");
  const [longDescription, setLongDescription] = useState("");
  const [selected, setSelected] = useState([]);
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState("");
  const [bannerUrl, setBannerUrl] = useState("");
  const [categoriesList, setCategoriesList] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/categories").then((r) => setCategoriesList(r.data || [])).catch(() => {});
  }, [open]);

  // Reset form whenever the modal opens for a new server.
  useEffect(() => {
    if (!open || !server) return;
    setDescription(server.description || "");
    setLongDescription(server.long_description || "");
    setSelected(server.categories || []);
    setTags(server.tags || []);
    setTagInput("");
    setBannerUrl(server.banner_url || "");
  }, [open, server]);

  if (!open || !server) return null;

  const toggleCat = (id) => {
    setSelected((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= 3) {
        toast.error("Max 3 categories");
        return prev;
      }
      return [...prev, id];
    });
  };

  const addTag = () => {
    const t = tagInput.trim().toLowerCase().replace(/[^a-z0-9-]/g, "").slice(0, 20);
    if (!t || tags.includes(t) || tags.length >= 8) return;
    setTags([...tags, t]);
    setTagInput("");
  };

  const save = async () => {
    if (description.trim().length < 1) {
      toast.error("Short description is required");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.put(`/servers/${server.id}`, {
        description: description.trim(),
        long_description: longDescription.trim(),
        categories: selected,
        tags,
        banner_url: bannerUrl.trim() || null,
      });
      toast.success("Listing updated");
      onSaved?.(data);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-start sm:items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="edit-server-dialog"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl my-8 rounded-2xl bg-[#0A0A0F] border border-white/10 p-6"
      >
        <div className="flex items-center justify-between mb-1">
          <h2 className="font-heading text-xl font-black">Edit listing</h2>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center"
            data-testid="edit-close-btn"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <p className="text-xs text-zinc-500 mb-5">
          Update how your server appears on Serverlistr. Name, icon and member
          count auto-sync from Discord.
        </p>

        <div className="space-y-5 max-h-[70vh] overflow-y-auto pr-1">
          <Field label="Short description" hint={`${description.length}/200`}>
            <input
              value={description}
              onChange={(e) => setDescription(e.target.value.slice(0, 200))}
              placeholder="One-line pitch shown on browse cards"
              data-testid="edit-description-input"
              className="w-full h-11 px-3 bg-[#12121A] border border-white/10 rounded-lg text-sm placeholder-zinc-600 focus:border-blurple focus:outline-none focus:ring-1 focus:ring-blurple"
            />
          </Field>

          <Field label="Full description" hint={`${longDescription.length}/2000`}>
            <textarea
              value={longDescription}
              onChange={(e) => setLongDescription(e.target.value.slice(0, 2000))}
              rows={5}
              placeholder="Tell people about your community — what's it for, what makes it great, who's it for?"
              data-testid="edit-long-description-input"
              className="w-full px-3 py-2 bg-[#12121A] border border-white/10 rounded-lg text-sm placeholder-zinc-600 focus:border-blurple focus:outline-none focus:ring-1 focus:ring-blurple resize-y"
            />
          </Field>

          <Field label="Banner URL (optional)" hint="Discord CDN image link">
            <input
              value={bannerUrl}
              onChange={(e) => setBannerUrl(e.target.value)}
              placeholder="https://cdn.discordapp.com/…"
              data-testid="edit-banner-input"
              className="w-full h-11 px-3 bg-[#12121A] border border-white/10 rounded-lg text-sm placeholder-zinc-600 focus:border-blurple focus:outline-none focus:ring-1 focus:ring-blurple"
            />
          </Field>

          <div>
            <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400">
              Categories · pick up to 3
            </label>
            <div className="mt-3 flex flex-wrap gap-2">
              {categoriesList.map((c) => {
                const on = selected.includes(c.id);
                return (
                  <button
                    key={c.id}
                    onClick={() => toggleCat(c.id)}
                    data-testid={`edit-cat-${c.id}`}
                    className={`inline-flex items-center gap-1 h-8 px-3 rounded-full text-xs font-semibold border transition-all ${
                      on
                        ? "bg-blurple/15 border-blurple text-white"
                        : "bg-white/5 border-white/10 text-zinc-400 hover:text-white"
                    }`}
                  >
                    {on && <Check className="w-3 h-3" />} {c.name}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400">
              Tags · up to 8
            </label>
            <div className="mt-2 flex gap-2">
              <input
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addTag();
                  }
                }}
                placeholder="e.g. minecraft, chill, lgbtq"
                data-testid="edit-tag-input"
                className="flex-1 h-10 px-3 bg-[#12121A] border border-white/10 rounded-lg text-sm placeholder-zinc-500 focus:border-blurple focus:outline-none focus:ring-1 focus:ring-blurple"
              />
              <button
                onClick={addTag}
                data-testid="edit-add-tag-btn"
                className="h-10 px-4 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-sm font-semibold"
              >
                Add
              </button>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {tags.map((t) => (
                <Badge
                  key={t}
                  className="bg-white/5 text-zinc-300 border border-white/10 gap-1"
                >
                  #{t}
                  <button onClick={() => setTags(tags.filter((x) => x !== t))}>
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="h-10 px-4 rounded-lg bg-white/5 hover:bg-white/10 text-sm font-semibold"
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={busy}
            data-testid="edit-save-btn"
            className="h-10 px-5 rounded-lg bg-blurple hover:bg-blurple-hover text-white font-bold disabled:opacity-50"
          >
            {busy ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <div className="flex items-baseline justify-between">
        <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400">
          {label}
        </label>
        {hint && <span className="text-[10px] text-zinc-600 font-mono">{hint}</span>}
      </div>
      <div className="mt-2">{children}</div>
    </div>
  );
}
